import { Button, Segmented, DatePicker, Grid, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';

const { RangePicker } = DatePicker;

export type DateRange = {
  from: Dayjs;
  to: Dayjs;
};

const OPTIONS = [
  'Today',
  'Yesterday',
  'Last 7 Days',
  'This Month',
  'Last Month',
  'Custom',
] as const;

export type DateRangeKey = (typeof OPTIONS)[number];

const DATE_RANGES: Record<Exclude<DateRangeKey, 'Custom'>, DateRange> = {
  Today: {
    from: dayjs().startOf('day'),
    to: dayjs().endOf('day'),
  },
  Yesterday: {
    from: dayjs().subtract(1, 'day').startOf('day'),
    to: dayjs().subtract(1, 'day').endOf('day'),
  },
  'Last 7 Days': {
    from: dayjs().subtract(6, 'day').startOf('day'),
    to: dayjs().endOf('day'),
  },
  'This Month': {
    from: dayjs().startOf('month'),
    to: dayjs().endOf('month'),
  },
  'Last Month': {
    from: dayjs().subtract(1, 'month').startOf('month'),
    to: dayjs().subtract(1, 'month').endOf('month'),
  },
};

type SegmentedDateProps = {
  onRangeChange?: (range: DateRange) => void;
  initialValue?: {
    from: string;
    to: string;
  };
};

function getInitialValue(fromDate?: string, toDate?: string): DateRangeKey {
  if (!fromDate || !toDate) return 'Today';

  const queryFrom = dayjs(fromDate).startOf('day');
  const queryTo = dayjs(toDate).endOf('day');
  for (const key in DATE_RANGES) {
    const range = DATE_RANGES[key as keyof typeof DATE_RANGES];
    if (queryFrom.isSame(range.from.startOf('day')) && queryTo.isSame(range.to.endOf('day'))) {
      return key as DateRangeKey;
    }
  }
  return 'Custom'; // fallback to custom if no match
}

export default function SegmentedDate({ onRangeChange, initialValue }: SegmentedDateProps) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const initialKey = getInitialValue(initialValue?.from, initialValue?.to);
  const [selected, setSelected] = useState<DateRangeKey>(initialKey);
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null] | null>(() => {
    if (initialKey === 'Custom' && initialValue?.from && initialValue?.to) {
      return [dayjs(initialValue.from), dayjs(initialValue.to)];
    }
    return null;
  });
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
  const keepPickerOpenAfterSelection = useRef(false);

  const handleChange = (value: string | number) => {
    const key = value as DateRangeKey;
    setSelected(key);
    if (key !== 'Custom') {
      const range = DATE_RANGES[key as keyof typeof DATE_RANGES];
      onRangeChange?.(range);
    }
  };

  const handleCustomCalendarChange = (
    dates: null | [Dayjs | null, Dayjs | null],
    _dateStrings: [string, string],
    info: { range: 'start' | 'end' },
  ) => {
    setCustomRange(dates);
    keepPickerOpenAfterSelection.current =
      info.range === 'end' && Boolean(dates?.[0] && dates?.[1]);
  };

  const handleCustomOpenChange = (open: boolean) => {
    if (!open && keepPickerOpenAfterSelection.current) {
      keepPickerOpenAfterSelection.current = false;
      return;
    }
    setIsCustomPickerOpen(open);
  };

  const handleCustomApply = () => {
    if (!customRange?.[0] || !customRange?.[1]) return;
    const [from, to] = customRange as [Dayjs, Dayjs];
    keepPickerOpenAfterSelection.current = false;
    setIsCustomPickerOpen(false);
    onRangeChange?.({
      from: from.startOf('day'),
      to: to.endOf('day'),
    });
  };

  return (
    <>
      {isMobile ? (
        <Select
          value={selected}
          options={OPTIONS.map((option) => ({ label: option, value: option }))}
          onChange={(value) => handleChange(value)}
          style={{ width: '100%', marginBottom: 8 }}
        />
      ) : (
        <Segmented
          style={{
            padding: '8px',
            backgroundColor: 'white',
            marginBottom: '8px',
            marginRight: '8px',
          }}
          value={selected}
          options={[...OPTIONS]}
          onChange={handleChange}
          className="date-filter"
        />
      )}

      {selected === 'Custom' && (
        <RangePicker
          style={{ marginBottom: 8, width: isMobile ? '100%' : undefined }}
          value={customRange}
          open={isCustomPickerOpen}
          onOpenChange={handleCustomOpenChange}
          onCalendarChange={handleCustomCalendarChange}
          allowClear
          inputReadOnly={isMobile}
          popupClassName="segmented-date-range-popup"
          placement="bottomLeft"
          renderExtraFooter={() => (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 10 }}>
              <Button
                type="primary"
                size="small"
                disabled={!customRange?.[0] || !customRange?.[1]}
                onClick={handleCustomApply}
              >
                Apply
              </Button>
            </div>
          )}
        />
      )}
    </>
  );
}
